import React, { createContext, useContext, useReducer, useCallback, ReactNode, useEffect } from 'react';
import type { Assistant, Chat, ChatFolder, LLMModel } from '../types';
import { api } from '../services/api';

// State Types
interface AppState {
  // Assistants
  assistants: Assistant[];
  selectedAssistant: Assistant | null;
  assistantsLoading: boolean;

  // Chats
  chats: Chat[];
  selectedChat: Chat | null;
  chatFolders: ChatFolder[];

  // Configuration
  selectedLLMModel: string;
  availableLLMModels: LLMModel[];
  modelsLoading: boolean;

  // UI State
  showConfigPanel: boolean;
}

// Action Types
type AppAction =
  | { type: 'SET_ASSISTANTS'; payload: Assistant[] }
  | { type: 'SELECT_ASSISTANT'; payload: Assistant | null }
  | { type: 'SET_ASSISTANTS_LOADING'; payload: boolean }
  | { type: 'SET_CHATS'; payload: Chat[] }
  | { type: 'SELECT_CHAT'; payload: Chat | null }
  | { type: 'ADD_CHAT'; payload: Chat }
  | { type: 'SET_CHAT_FOLDERS'; payload: ChatFolder[] }
  | { type: 'SET_LLM_MODEL'; payload: string }
  | { type: 'SET_AVAILABLE_MODELS'; payload: LLMModel[] }
  | { type: 'SET_MODELS_LOADING'; payload: boolean }
  | { type: 'TOGGLE_CONFIG_PANEL' };

// Static fallback models shown before API responds
const FALLBACK_MODELS: LLMModel[] = [
  { id: 'assistant-default', name: 'assistant-default', displayName: 'Assistant Default' },
  { id: 'gpt-4o', name: 'gpt-4o', displayName: 'GPT-4o', provider: 'openai' },
  { id: 'gpt-4o-mini', name: 'gpt-4o-mini', displayName: 'GPT-4o-mini', provider: 'openai' },
  { id: 'gpt-4.1', name: 'gpt-4.1', displayName: 'GPT-4.1', provider: 'openai' },
  { id: 'claude-sonnet-4-20250514', name: 'claude-sonnet-4-20250514', displayName: 'Claude Sonnet 4', provider: 'anthropic' },
  { id: 'claude-opus-4-20250514', name: 'claude-opus-4-20250514', displayName: 'Claude Opus 4', provider: 'anthropic' },
  { id: 'grok-4-latest', name: 'grok-4-latest', displayName: 'Grok-4', provider: 'grok' },
];

// Initial State
const initialState: AppState = {
  assistants: [],
  selectedAssistant: null,
  assistantsLoading: false,
  chats: [],
  selectedChat: null,
  chatFolders: [],
  selectedLLMModel: 'assistant-default',
  availableLLMModels: FALLBACK_MODELS,
  modelsLoading: false,
  showConfigPanel: false,
};

// Reducer
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_ASSISTANTS':
      return { ...state, assistants: action.payload };
    case 'SELECT_ASSISTANT':
      return { ...state, selectedAssistant: action.payload };
    case 'SET_ASSISTANTS_LOADING':
      return { ...state, assistantsLoading: action.payload };
    case 'SET_CHATS':
      return { ...state, chats: action.payload };
    case 'SELECT_CHAT':
      return { ...state, selectedChat: action.payload };
    case 'ADD_CHAT':
      return { ...state, chats: [action.payload, ...state.chats] };
    case 'SET_CHAT_FOLDERS':
      return { ...state, chatFolders: action.payload };
    case 'SET_LLM_MODEL':
      return { ...state, selectedLLMModel: action.payload };
    case 'SET_AVAILABLE_MODELS':
      return { ...state, availableLLMModels: action.payload };
    case 'SET_MODELS_LOADING':
      return { ...state, modelsLoading: action.payload };
    case 'TOGGLE_CONFIG_PANEL':
      return { ...state, showConfigPanel: !state.showConfigPanel };
    default:
      return state;
  }
}

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Actions
  setAssistants: (assistants: Assistant[]) => void;
  selectAssistant: (assistant: Assistant | null) => void;
  setChats: (chats: Chat[]) => void;
  selectChat: (chat: Chat | null) => void;
  addChat: (chat: Chat) => void;
  setLLMModel: (model: string) => void;
  toggleConfigPanel: () => void;
  refreshModels: () => Promise<void>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const setAssistants = useCallback((assistants: Assistant[]) => {
    dispatch({ type: 'SET_ASSISTANTS', payload: assistants });
  }, []);

  const selectAssistant = useCallback((assistant: Assistant | null) => {
    dispatch({ type: 'SELECT_ASSISTANT', payload: assistant });
  }, []);

  const setChats = useCallback((chats: Chat[]) => {
    dispatch({ type: 'SET_CHATS', payload: chats });
  }, []);

  const selectChat = useCallback((chat: Chat | null) => {
    dispatch({ type: 'SELECT_CHAT', payload: chat });
  }, []);

  const addChat = useCallback((chat: Chat) => {
    dispatch({ type: 'ADD_CHAT', payload: chat });
  }, []);

  const setLLMModel = useCallback((model: string) => {
    dispatch({ type: 'SET_LLM_MODEL', payload: model });
  }, []);

  const toggleConfigPanel = useCallback(() => {
    dispatch({ type: 'TOGGLE_CONFIG_PANEL' });
  }, []);

  /**
   * Fetch available models from the backend and populate the selector.
   * Falls back to static list silently on error.
   */
  const refreshModels = useCallback(async () => {
    dispatch({ type: 'SET_MODELS_LOADING', payload: true });
    try {
      const response = await api.getAIModels();
      if (response.success && response.data.models?.length > 0) {
        // Always prepend "Assistant Default" so users can let the assistant pick its own model
        const models: LLMModel[] = [
          { id: 'assistant-default', name: 'assistant-default', displayName: 'Assistant Default' },
          ...response.data.models.map(m => ({
            id: m.id,
            name: m.name,
            displayName: m.name,
            provider: m.provider,
            description: m.description,
          })),
        ];
        dispatch({ type: 'SET_AVAILABLE_MODELS', payload: models });
      }
    } catch {
      // Keep fallback models — no visible error needed
    } finally {
      dispatch({ type: 'SET_MODELS_LOADING', payload: false });
    }
  }, []);

  // Fetch models once on mount
  useEffect(() => {
    refreshModels();
  }, [refreshModels]);

  // Ensure selected model is always valid against current available models.
  useEffect(() => {
    const availableIds = new Set(state.availableLLMModels.map(m => m.id));
    if (!availableIds.has(state.selectedLLMModel)) {
      const fallback = state.availableLLMModels.find(m => m.id === 'assistant-default')?.id
        || state.availableLLMModels[0]?.id
        || 'assistant-default';
      dispatch({ type: 'SET_LLM_MODEL', payload: fallback });
    }
  }, [state.availableLLMModels, state.selectedLLMModel]);

  const value: AppContextType = {
    state,
    dispatch,
    setAssistants,
    selectAssistant,
    setChats,
    selectChat,
    addChat,
    setLLMModel,
    toggleConfigPanel,
    refreshModels,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// Hook
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

export default AppContext;
